/**
 * JSON with object keys in a fixed order. `JSON.stringify` is order-sensitive,
 * and a value that has been to Firestore and back does not necessarily carry its
 * keys in the order it was written with — so comparing two equal values with it
 * can report a difference that is not there, which is enough to leave an
 * autosaving editor permanently convinced it has unsaved work.
 *
 * Matches `JSON.stringify` in dropping object properties whose value is
 * `undefined`, so a field that is absent and a field that is explicitly
 * undefined compare equal, as they do once stored.
 */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, entry]) => entry !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stableStringify(entry)}`).join(',')}}`
}

/** Deep equality for plain JSON-shaped values, insensitive to key order. */
export function sameValue(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b)
}
